/**
 * 身份模块：欢迎/注册/登录/入住激活/设置页面、内容使用说明页（/settings/data-use）+ SessionService。
 * live：用户名+密码（服务端 scrypt、可吊销会话 cookie + CSRF）；入住阶段与设置读写真实接口。
 * fixture：演示身份——不创建假账号；入住阶段视为已入住，设置只在本页内存里演示。
 */
import type { AuthCapabilities, CitySearchResult, CityStatus, HomePlaceView, OnboardingState, PickResult, SessionState, SettingsView } from "@/shared/contracts";
import { ApiError, isApiError } from "@/shared/api/errors";
import { defineModule } from "@/shared/modules/types";
import { LoginPage, MoveInPage, RegisterPage, SettingsPage, WelcomePage } from "./pages";
import { ArrivingPage } from "./arriving/ArrivingPage";
import { DataUsePage } from "./dataUse/DataUsePage";
import type { DataServicesView } from "./dataUse/dataServices";

const FIXTURE_ONBOARDING: OnboardingState = {
  step: "active",
  pet_id: "fx-pet-mochi",
  home_id: "fx-home-1",
  reception_session_id: null,
  reception_skipped: false,
  home_activated_at: null,
  pet_origin: "own_pet",
};

/** 演示的“目前在用的外部服务”（DATA-USE-COPY-01）：一项有名字、一项没有名字，演示里两种显示都看得到；名字是演示用的，不是真实服务商 */
const FIXTURE_DATA_SERVICES: DataServicesView = {
  services: [
    { purpose: "conversation", display_name: "演示语言服务", contract_entity: null },
    { purpose: "images", display_name: null, contract_entity: null },
  ],
};

export default defineModule({
  id: "identity",
  routes: [{ path: "settings", element: <SettingsPage /> }],
  bareRoutes: [
    { path: "welcome", element: <WelcomePage /> },
    { path: "register", element: <RegisterPage /> },
    { path: "login", element: <LoginPage /> },
    { path: "onboarding/move-in", element: <MoveInPage /> },
    // 到家等待页（6796 起草，10-03）：入住成功后先到这里，TA 的样子齐了再进小窝；离开后凭 ?pet= 回来。不依赖当前家庭，和入住页一样不挂外壳
    { path: "arriving", element: <ArrivingPage /> },
    // 内容使用说明（DATA-USE-COPY-01）：不挂外壳、不过入住守卫——入住前在建宠页点“查看内容使用说明”也能看（接口是公开的）
    { path: "settings/data-use", element: <DataUsePage /> },
  ],
  services: {
    session: {
      live: ({ api }) => ({
        current: async () => {
          try {
            return await api.request<SessionState>("/session");
          } catch (error) {
            // cookie 过期或已吊销：当作未登录（服务端已拒绝该会话），由守卫带回欢迎页重新登录。
            if (isApiError(error) && error.code === "SESSION_EXPIRED") return { authenticated: false, user: null, csrf_required: false, expires_at: null, onboarding: null };
            throw error;
          }
        },
        register: (username, password, displayName, entry) => api.request<SessionState>("/auth/register", { method: "POST", body: { username, password, display_name: displayName ?? null, entry: entry ?? null } }),
        login: (username, password) => api.request<SessionState>("/auth/login", { method: "POST", body: { username, password } }),
        logout: () => api.request<void>("/auth/logout", { method: "POST" }),
        // 能力表（AUTH-RECOVERY-UI-116）：注册页那句找回按它说；/meta 里 identity.email_recovery 那条还是旧的，不看它
        authCapabilities: () => api.request<AuthCapabilities>("/auth/capabilities"),
        onboarding: () => api.request<OnboardingState>("/onboarding"),
        // city 只在选了 habitat 又选了城市时才放进请求体（不写 city: null），没选 habitat 的入住和旧后端都不受影响（入住选城市，6796 起草）
        moveIn: (publicPosts, habitat, petId, city) =>
          api.request<OnboardingState>("/onboarding/move-in", { method: "POST", body: { public_posts: publicPosts, habitat: habitat ?? null, pet_id: petId ?? null, ...(habitat && city ? { city } : {}) } }),
        homePlace: (petId) => api.request<HomePlaceView>("/home/place", { query: { pet_id: petId ?? undefined } }),
        // 入住选城（UI-MOVEIN-CITY-01）：三种选法都只读；入住带 city_id，不再带城市名（habitat 是这座城里的生活环境）
        searchCities: (q) => api.request<CitySearchResult>("/cities/search", { query: { q } }),
        recommendCities: (habitat, batch, petId) => api.request<PickResult>("/cities/recommend", { query: { habitat: habitat ?? undefined, batch, pet_id: petId ?? undefined } }),
        randomCities: (batch, petId) => api.request<PickResult>("/cities/random", { query: { batch, pet_id: petId ?? undefined } }),
        cityStatus: (cityId) => api.request<CityStatus>(`/cities/${encodeURIComponent(cityId)}`),
        moveInCity: (publicPosts, cityId, habitat, petId) =>
          api.request<OnboardingState>("/onboarding/move-in", { method: "POST", body: { public_posts: publicPosts, city_id: cityId, habitat, pet_id: petId ?? null } }),
        // 设置里的简介、公开范围、公开动态是这只宠物的：一家有两只时不带 pet_id，读的是“第一只”，改则 409 pet_required。
        settings: (petId) => api.request<SettingsView>("/settings", { query: { pet_id: petId } }),
        updateSettings: (patch, petId) => api.request<SettingsView>("/settings", { method: "PATCH", query: { pet_id: petId }, body: patch }),
        // 内容使用说明页：实际在用的外部服务（公开只读；回包类型等 I108 契约同步换掉，见 ./dataUse/dataServices.ts）
        dataServices: () => api.request<DataServicesView>("/meta/data-services"),
      }),
      fixture: () => {
        let settings: SettingsView = { username: null, display_name: "演示主人", public_posts: true, profile_visibility: "public", bio: "演示宠物的简介", intent_layer_mode: "off" };
        return {
          current: async () => ({ authenticated: false, user: null, csrf_required: false, expires_at: null, onboarding: null }),
          register: async () => {
            throw ApiError.capability("identity.password_registration", "演示模式不创建账号。");
          },
          login: async () => {
            throw ApiError.capability("identity.password_registration", "演示模式不提供登录。");
          },
          logout: async () => undefined,
          onboarding: async () => FIXTURE_ONBOARDING,
          moveIn: async () => FIXTURE_ONBOARDING,
          homePlace: async () => { throw ApiError.capability("home.place", "演示模式不选择正式住处。"); },
          searchCities: async () => { throw ApiError.capability("cities.search", "演示模式不选城市。"); },
          recommendCities: async () => { throw ApiError.capability("cities.search", "演示模式不选城市。"); },
          randomCities: async () => { throw ApiError.capability("cities.search", "演示模式不选城市。"); },
          cityStatus: async () => { throw ApiError.capability("cities.search", "演示模式不选城市。"); },
          moveInCity: async () => FIXTURE_ONBOARDING,
          settings: async () => settings,
          dataServices: async () => FIXTURE_DATA_SERVICES,
          updateSettings: async (patch) => {
            settings = {
              ...settings,
              public_posts: patch.public_posts ?? settings.public_posts,
              profile_visibility: patch.profile_visibility ?? settings.profile_visibility,
              bio: patch.bio ?? settings.bio,
            };
            return settings;
          },
        };
      },
    },
  },
});
